'use client';

import Link from '@/routing/AppLink';
import { useTranslation } from 'next-i18next/pages';
import type { ReactNode } from 'react';

import ResponsiveLayout from '@/components/apps/layouts/responsive/Layout';
import {
  buildIndustrialCaseListPath,
  type IndustrialCaseListSolution,
} from '@/components/apps/pages/solution/industrial/industrialCaseRoutes';
import VisuallyHiddenPageTitle from '@/components/apps/ui/a11y/VisuallyHiddenPageTitle';

/**
 * Where the closing action sends a reader.
 *
 * Built here as a literal because this branch does not yet carry the shared
 * inquiry-link helper that other case surfaces were moved onto. Keeping it in
 * one place means the swap is a single edit when the branches meet.
 */
const INQUIRY_HREF = '/inquiry?interest=poc-terms&source=cases-list';

const SOLUTION_TABS: ReadonlyArray<{
  id: IndustrialCaseListSolution;
  labelKey: string;
}> = [
  { id: 'all', labelKey: '전체' },
  { id: 'industrial', labelKey: 'Industrial 솔루션' },
  { id: 'safety', labelKey: 'Safety 솔루션' },
];

const CASE_LIST_COPY_KEYS = {
  all: {
    title: 'Listen AI 고객 사례',
    inquiryTitle: '우리 라인에 적용 가능한지 확인해보세요',
    inquiryDescription:
      '검사 대상 소리와 생산 조건을 알려주시면 적용 범위와 필요한 데이터를 정리해 드립니다',
  },
  industrial: {
    title: '소리 AI로 검사 공정을 바꾼 고객 사례',
    inquiryTitle: '우리 라인에 적용 가능한지 확인해보세요',
    inquiryDescription:
      '검사 대상 소리와 생산 조건을 알려주시면 적용 범위와 필요한 데이터를 정리해 드립니다',
  },
  safety: {
    title: '소리 AI로 안전을 지킨 고객 사례',
    inquiryTitle: '우리 라인에 적용 가능한지 확인해보세요',
    inquiryDescription:
      '검사 대상 소리와 생산 조건을 알려주시면 적용 범위와 필요한 데이터를 정리해 드립니다',
  },
} as const satisfies Record<
  IndustrialCaseListSolution,
  {
    title: string;
    inquiryTitle: string;
    inquiryDescription: string;
  }
>;

type Props = {
  activeSolution: IndustrialCaseListSolution;
  caseList: ReactNode;
};

/**
 * ### IndustrialCaseListPageClient
 *
 * Renders the immediately available customer-case frame for one solution.
 *
 * The server streams `caseList` independently, so the title and tabs stay
 * responsive while a board request is pending. Tabs remain URL links carrying
 * `?solution=`, preserving shareable and crawlable views.
 *
 * @param props The selected solution and its streamed case-list region
 * @returns The customer-case page frame
 */
export default function IndustrialCaseListPageClient({
  activeSolution,
  caseList,
}: Props) {
  const { t } = useTranslation('common');
  const copyKeys = CASE_LIST_COPY_KEYS[activeSolution];

  return (
    <>
      <VisuallyHiddenPageTitle>{t('고객 사례')}</VisuallyHiddenPageTitle>

      <ResponsiveLayout mobilePadding={{ top: '0', bottom: '0' }}>
        <div className="mx-auto w-full max-w-[1248px] border-x border-[#e8e8e5] px-5 py-16 text-apps-brandGrayBlack md:px-10 md:py-24 lg:px-20">
          <header className="max-w-[760px]">
            <p className="text-sm font-semibold leading-[1.4] text-[#686864]">
              {t(copyKeys.title)}
            </p>
            <h1 className="mt-4 text-[clamp(34px,4.6vw,56px)] font-semibold leading-[1.1] tracking-[-0.04em] text-balance">
              {t('ListenAI 고객사례')}
            </h1>
          </header>

          <nav
            className="mt-10 grid grid-cols-2 gap-2 md:flex md:flex-wrap"
            aria-label={t('사례 목록')}
          >
            {SOLUTION_TABS.map((tab) => {
              const isActive = tab.id === activeSolution;

              return (
                <Link
                  className={`inline-flex min-h-11 min-w-0 items-center justify-center border px-2 text-center text-[13px] font-semibold leading-[1.25] whitespace-nowrap transition-colors duration-200 ease-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-apps-corePrimary01 motion-reduce:transition-none md:px-5 md:text-sm ${
                    isActive
                      ? 'border-apps-brandGrayBlack bg-apps-brandGrayBlack text-apps-coreGrayWhite'
                      : 'border-[#dcdcd7] text-[#4f4f4b] hover:border-apps-brandGrayBlack hover:text-apps-brandGrayBlack'
                  }`}
                  href={
                    tab.id === 'all'
                      ? buildIndustrialCaseListPath()
                      : buildIndustrialCaseListPath(tab.id)
                  }
                  key={tab.id}
                  prefetch={false}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {t(tab.labelKey)}
                </Link>
              );
            })}
          </nav>

          {caseList}

          <section className="mt-16 grid gap-8 border-t border-[#d8d8d4] pt-12 md:grid-cols-[minmax(0,1fr)_240px] md:items-end">
            <div>
              <h2 className="text-[clamp(24px,3vw,34px)] font-semibold leading-[1.18] tracking-[-0.03em] text-balance">
                {t(copyKeys.inquiryTitle)}
              </h2>
              <p className="mt-4 max-w-[560px] text-[15px] font-medium leading-[1.7] text-[#4f4f4b] [word-break:keep-all] md:text-base">
                {t(copyKeys.inquiryDescription)}
              </p>
            </div>
            <Link
              className="inline-flex min-h-14 w-full items-center justify-center bg-apps-brandGrayBlack px-5 text-sm font-semibold text-apps-coreGrayWhite transition-colors duration-250 ease-out hover:bg-apps-corePrimary01 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-apps-corePrimary01 motion-reduce:transition-none"
              href={INQUIRY_HREF}
              prefetch={false}
            >
              {t('문의하기')}
            </Link>
          </section>
        </div>
      </ResponsiveLayout>
    </>
  );
}
